import {COUNTRY_CODES} from '@/utils/constants/globalConstants';
import {VALIDATION_TYPES} from '@/utils/constants/commonConstants';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {
  getProductGtinSlots,
  isProductGroupQuantityType
} from '@/utils/productGroupQuantity';

const CSV_RULE_BASE = {
  type: 'text/csv',
  size: 25600
};

const sharedColumns = [
  {
    name: 'Store Segments',
    required: false,
    type: 'string',
    minChar: 0,
    maxChar: 255
  },
  {
    name: 'Previous Year Base Revenue (optional)',
    required: false,
    type: 'number'
  }
];

const storeIdColumn = {
  name: 'StoreId',
  required: true,
  type: 'alphanumeric_hyphen',
  minChar: 1,
  maxChar: 255
};

/**
 * Get spend column label adjusted for Egypt.
 */
const getSpendColumn = (label, countryCode) => ({
  name:
    countryCode === COUNTRY_CODES.EGYPT
      ? label.replace('Dollar Spend', 'Number of Cases')
      : label,
  required: true,
  type: 'number'
});

/**
 * Builds columns for main target rules.
 */
const buildMainTargetColumns = (countryCode) => [
  storeIdColumn,
  getSpendColumn('Dollar Spend', countryCode),
  {
    name: 'Points To Award',
    required: true,
    type: 'number'
  },
  ...sharedColumns
];

const buildProductGroupQuantityColumns = (productGtins = []) => [
  storeIdColumn,
  ...getProductGtinSlots(productGtins).map((slot) => ({
    name: `Gtin${slot}Qty`,
    required: true,
    type: VALIDATION_TYPES.POSITIVE_INTEGER
  })),
  {
    name: 'PointsToAward',
    required: true,
    type: VALIDATION_TYPES.POSITIVE_INTEGER
  }
];

/**
 * Builds layered target rules based on number of layers.
 */
const buildLayeredColumns = (layerCount, countryCode) => {
  const layers = [];
  for (let i = 1; i <= layerCount; i++) {
    layers.push(getSpendColumn(`Layer ${i} Dollar Spend`, countryCode), {
      name: `Layer ${i} Points To Award`,
      required: true,
      type: 'number'
    });
  }
  return [storeIdColumn, ...layers, ...sharedColumns];
};

/**
 * Builds pre-requisite rules columns.
 */
const buildPreReqColumns = (countryCode) => [
  storeIdColumn,
  getSpendColumn('Parent Dollar Spend', countryCode),
  {
    name: 'Parent Points to Award',
    required: true,
    type: 'number'
  },
  getSpendColumn('Child Dollar Spend', countryCode),
  {
    name: 'Child Points to Award',
    required: true,
    type: 'number'
  },
  ...sharedColumns
];

/**
 * Builds columns for early payment target rules.
 */
const buildEarlyPaymentColumns = () => [
  {
    name: 'StoreID*',
    required: true,
    type: 'string',
    minChar: 1,
    maxChar: 255
  },
  {
    name: 'Associated Target Linkage* (Yes/No)',
    required: true,
    type: 'yes_no'
  },
  {
    name: 'Comments (Optional)',
    required: false,
    type: 'string',
    minChar: 0,
    maxChar: 255
  }
];

/**
 * Builds columns for drop size target rules.
 */
const buildDropSizeColumns = () => [
  {
    name: 'Store ID',
    required: true,
    type: 'alphanumeric_hyphen',
    minChar: 1,
    maxChar: 255
  },
  {
    name: 'Associated Target Linkage (Yes/No)',
    required: true,
    type: 'yes_no'
  },
  {
    name: 'Drop Size %',
    required: false,
    requiredIf: {column: 'Associated Target Linkage (Yes/No)', value: 'Yes'},
    type: 'number'
  },
  {
    name: 'Drop Size Value',
    required: false,
    requiredIf: {column: 'Associated Target Linkage (Yes/No)', value: 'No'},
    type: 'number'
  },
  {
    name: 'Invoice Value %',
    required: true,
    type: 'number'
  },
  {
    name: 'Min count of Invoice',
    required: true,
    type: 'number'
  },
  {
    name: 'Max count of Invoice (Optional)',
    required: false,
    type: 'number'
  },
  {
    name: 'Comments (Optional)',
    required: false,
    type: 'string',
    minChar: 0,
    maxChar: 255
  }
];

/**
 * Generates full rule object from columns.
 */
const createRule = (columns, additionalRules = {}) => ({
  ...CSV_RULE_BASE,
  ...additionalRules,
  columns
});

/**
 * Main function to get validation rules based on target type.
 */
const getTargetRules = (formData) => {
  const {
    targetType,
    numberLevel = 1,
    numberMileStone = 1,
    countryCode = '',
    productGtins = []
  } = formData;

  const layerCount = numberLevel * numberMileStone || 1;

  const ruleMap = {
    [TARGET_TYPES.MonthlyTargetLayered]: () =>
      createRule(buildLayeredColumns(layerCount, countryCode)),
    [TARGET_TYPES.ProductTargetLayered]: () =>
      createRule(buildLayeredColumns(layerCount, countryCode)),
    [TARGET_TYPES.MainTarget]: () =>
      createRule(buildMainTargetColumns(countryCode)),
    [TARGET_TYPES.ProductTarget]: () =>
      createRule(buildMainTargetColumns(countryCode)),
    [TARGET_TYPES.MonthlyTargetPrerequistite]: () =>
      createRule(buildPreReqColumns(countryCode)),
    [TARGET_TYPES.ProductTargetPrerequisite]: () =>
      createRule(buildPreReqColumns(countryCode)),
    [TARGET_TYPES.EarlyPaymentTarget]: () =>
      createRule(buildEarlyPaymentColumns()),
    [TARGET_TYPES.DropSizeTarget]: () => createRule(buildDropSizeColumns())
  };

  if (isProductGroupQuantityType(targetType)) {
    const columns = buildProductGroupQuantityColumns(productGtins);
    return createRule(columns, {
      requiredHeaders: columns.map(({name}) => name)
    });
  }

  const ruleGenerator = ruleMap[targetType];

  if (!ruleGenerator) {
    //console.warn('[getTargetRules] No rules found for:', targetType)
    return null;
  }

  const rules = ruleGenerator();
  //console.debug('[getTargetRules] Rules for:', targetType, ':', rules)
  return rules;
};

export default getTargetRules;
